import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { userApi, taskApi, announcementApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useAuthStore } from '../../stores/authStore';
import { Card, CardHeader, Badge, ErrorAlert, EmptyState, SkeletonCard } from '../../components/ui';
import { TaskListCard } from '../../components/tasks';
import { getReviewTypeConfig } from '../../lib/reviewTypes';
import {
  ClipboardList,
  ArrowRight,
  Wallet,
  CheckCircle2,
  Gift,
  Rocket,
  LayoutGrid,
  Megaphone,
  Mail,
} from 'lucide-react';

function getRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getStatusBadgeVariant(status: string): 'blue' | 'yellow' | 'green' | 'red' | 'gray' {
  switch (status) {
    case 'claimed': return 'blue';
    case 'submitted': return 'yellow';
    case 'approved': return 'green';
    case 'rejected': return 'red';
    default: return 'gray';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'claimed': return 'Claimed';
    case 'submitted': return 'Submitted';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    default: return status;
  }
}

function getStatusCircleColors(status: string): { bg: string; color: string } {
  switch (status) {
    case 'claimed': return { bg: '#E8F4FD', color: '#4750DD' };
    case 'submitted': return { bg: '#FEF6E0', color: '#E89B0C' };
    case 'approved': return { bg: '#E6F9F0', color: '#05C274' };
    case 'rejected': return { bg: '#FDE8ED', color: '#C4214A' };
    default: return { bg: '#F0F4F8', color: '#697485' };
  }
}

export function DashboardPage() {
  const { user } = useAuthStore();

  const { data: myTasksData, isError: myTasksError, refetch: refetchMyTasks } = useApiQuery({
    queryKey: ['my-tasks'],
    queryFn: () => userApi.getMyTasks(1, 100),
    refetchOnMount: 'always',
  });

  const { data: creditsData, isError: creditsError, refetch: refetchCredits } = useApiQuery({
    queryKey: ['credits'],
    queryFn: () => userApi.getCredits(1, 5),
    refetchOnMount: 'always',
  });

  const { data: availableTasksData, isLoading: availableTasksLoading } = useApiQuery({
    queryKey: ['available-tasks-dashboard'],
    queryFn: () => taskApi.list(1, 6, 'available'),
    refetchOnMount: 'always',
  });

  const { data: referralsData } = useApiQuery({
    queryKey: ['referrals-dashboard'],
    queryFn: () => userApi.getReferrals(),
    refetchOnMount: 'always',
  });

  const { data: announcementsData } = useApiQuery({
    queryKey: ['announcements-dashboard'],
    queryFn: () => announcementApi.list(),
    refetchOnMount: 'always',
  });
  const latestAnnouncements = (announcementsData?.announcements ?? []).slice(0, 3);

  const hasError = myTasksError || creditsError;

  // Computed stats
  const stats = useMemo(() => {
    const participations = myTasksData?.participations || [];
    const activeCount = participations.filter(p => p.status === 'claimed' || p.status === 'submitted').length;
    const approvedCount = participations.filter(p => p.status === 'approved').length;
    const rejectedCount = participations.filter(p => p.status === 'rejected').length;
    const pendingReviewCount = participations.filter(p => p.status === 'submitted').length;
    const totalReviewed = approvedCount + rejectedCount;
    const approvalRate = totalReviewed > 0 ? Math.round((approvedCount / totalReviewed) * 100) : 0;

    return { activeCount, approvedCount, pendingReviewCount, approvalRate, totalReviewed };
  }, [myTasksData]);

  // Recent activity: all participations sorted by most recent
  const recentActivity = useMemo(() => {
    const participations = myTasksData?.participations || [];
    return [...participations]
      .sort((a, b) => {
        const aDate = a.submitted_at || a.created_at;
        const bDate = b.submitted_at || b.created_at;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      })
      .slice(0, 5);
  }, [myTasksData]);

  const firstName = user?.full_name?.split(' ')[0] || 'User';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {firstName}</h1>
          <p className="text-sm text-gray-500 mt-1">Here's your activity overview</p>
        </div>
      </div>

      {hasError && (
        <ErrorAlert
          message="Failed to load dashboard data"
          onRetry={() => { refetchMyTasks(); refetchCredits(); }}
        />
      )}

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl p-3.5" style={{ backgroundColor: '#FEF6E0' }}>
          <div className="space-y-1.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FDECC0' }}>
              <Wallet className="w-4 h-4" style={{ color: '#E89B0C' }} />
            </div>
            <p className="text-[13px] font-medium text-gray-500">Credit Balance</p>
            <p className="text-2xl font-bold" style={{ color: '#212161' }}>{creditsData?.balance || 0}</p>
            <p className="text-[11px]" style={{ color: '#92400E' }}>credits</p>
          </div>
        </div>

        <div className="rounded-xl p-3.5" style={{ backgroundColor: '#E8F4FD' }}>
          <div className="space-y-1.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C5E4F9' }}>
              <ClipboardList className="w-4 h-4" style={{ color: '#4750DD' }} />
            </div>
            <p className="text-[13px] font-medium text-gray-500">Active Tasks</p>
            <p className="text-2xl font-bold" style={{ color: '#212161' }}>{stats.activeCount}</p>
            <p className="text-[11px]" style={{ color: '#4750DD' }}>{stats.pendingReviewCount} pending review</p>
          </div>
        </div>

        <div className="rounded-xl p-3.5" style={{ backgroundColor: '#E6F9F0' }}>
          <div className="space-y-1.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C0F0DA' }}>
              <CheckCircle2 className="w-4 h-4" style={{ color: '#05C274' }} />
            </div>
            <p className="text-[13px] font-medium text-gray-500">Completed</p>
            <p className="text-2xl font-bold" style={{ color: '#212161' }}>{stats.approvedCount}</p>
            <p className="text-[11px]" style={{ color: '#05C274' }}>+{stats.approvedCount} approved</p>
          </div>
        </div>

        <Link to="/referral" className="rounded-xl p-3.5 block hover:opacity-80 transition-opacity" style={{ backgroundColor: '#EDEAFC' }}>
          <div className="space-y-1.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D5D0F7' }}>
              <Gift className="w-4 h-4" style={{ color: '#4750DD' }} />
            </div>
            <p className="text-[13px] font-medium text-gray-500">Referrals</p>
            <p className="text-2xl font-bold" style={{ color: '#212161' }}>{referralsData?.total ?? 0}</p>
            <p className="text-[11px]" style={{ color: '#4750DD' }}>{referralsData?.completed ?? 0} completed</p>
          </div>
        </Link>
      </div>

      {/* Announcements Banner */}
      {latestAnnouncements.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-bold text-gray-900">Announcements</h2>
            </div>
            <Link
              to="/announcements"
              className="text-[13px] font-medium text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
          <div className="space-y-2">
            {latestAnnouncements.map((a) => (
              <Link
                key={a.id}
                to={`/announcements/${a.id}`}
                className="flex items-center gap-3 bg-primary-50 border border-primary-100 rounded-xl px-4 py-3 hover:bg-primary-100 transition-colors"
              >
                <Megaphone className="w-4 h-4 text-primary-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-primary-900 truncate">{a.title}</p>
                  {a.preview && (
                    <p className="text-xs text-primary-700 mt-0.5 line-clamp-1">{a.preview}</p>
                  )}
                </div>
                <span className="text-xs text-primary-500 whitespace-nowrap flex-shrink-0">
                  {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Available Tasks */}
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-bold text-gray-900">Available Tasks</h2>
          </div>
          <p className="text-[13px] font-medium text-gray-500">Claim a task and start earning credits!</p>
        </div>
        {availableTasksLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(3)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (availableTasksData?.tasks?.length ?? 0) === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No tasks available"
            description="There are no tasks available at the moment. Check back later."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...availableTasksData!.tasks].sort((a, b) => {
              const order = (t: typeof a) => {
                const isActive = t.status === 'active' && new Date(t.end_date) >= new Date();
                if (!isActive) return 4;
                const s = t.user_participation_status;
                if (s === 'claimed') return 0;
                if (s === 'submitted') return 1;
                if (s === 'approved') return 3;
                return 2;
              };
              return order(a) - order(b);
            }).slice(0, 3).map((task, index) => (
              <div key={task.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                <TaskListCard task={task} />
              </div>
            ))}
            <Link
              to="/tasks"
              className="flex flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-primary-300 bg-white p-5 hover:border-primary-500 hover:bg-primary-50/50 transition-all h-full min-h-[200px]"
            >
              <div className="w-[52px] h-[52px] rounded-full bg-primary-50 flex items-center justify-center">
                <LayoutGrid className="w-6 h-6 text-primary-600" />
              </div>
              <span className="text-[15px] font-bold text-primary-600">Browse All Tasks</span>
              <span className="text-xs font-medium text-gray-400 text-center">Discover more tasks{'\n'}and earn credits</span>
            </Link>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <Card className="overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <Link
                to="/my-tasks"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </CardHeader>
          {recentActivity.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={ClipboardList}
                title="No activity yet"
                description="Your recent task activity will appear here."
                action={
                  <Link
                    to="/tasks"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Browse available tasks
                  </Link>
                }
              />
            </div>
          ) : (
            <div>
              {recentActivity.map((participation, index) => {
                const statusColors = getStatusCircleColors(participation.status);
                const reviewConfig = getReviewTypeConfig(participation.review_type);
                const ReviewIcon = reviewConfig.icon;
                const activityDate = participation.submitted_at || participation.created_at;

                return (
                  <Link
                    key={participation.id}
                    to={`/tasks/${participation.task_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: statusColors.bg }}
                      >
                        <ReviewIcon className="w-4 h-4" style={{ color: statusColors.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{participation.task_name}</p>
                        <p className="text-xs text-gray-500">
                          {getRelativeTime(activityDate)}
                          {participation.google_email && (
                            <span className="ml-2 inline-flex items-center gap-1 text-gray-400">
                              <Mail className="w-3 h-3" />
                              <span className="truncate">{participation.google_email}</span>
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Badge variant={getStatusBadgeVariant(participation.status)}>
                      {getStatusLabel(participation.status)}
                    </Badge>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
